import { Identifier } from 'dnd-core';
import Immutable from 'immutable';
import React from 'react';
import type { ConnectDropTarget, DropTargetConnector, DropTargetMonitor, DropTargetSpec } from 'react-dnd';
import { DropTarget } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';

import Button from 'sharedreact/components/Button';
import DropZone from 'sharedreact/components/DropZone';
import SearchField from 'sharedreact/components/SearchField';
import Tab from 'sharedreact/components/Tab';
import Toolbar from 'sharedreact/components/Toolbar';
import ToolbarGroup from 'sharedreact/components/ToolbarGroup';
import ToolbarTitle from 'sharedreact/components/ToolbarTitle';
import askConfirmation from 'sharedreact/components/askConfirmation';
import SortOrder from 'sharedreact/data/SortOrder';
import UploadedFile from 'sharedreact/data/UploadedFile';
import { RangeType } from 'sharedreact/stores/generic/Type';
import { translate } from 'sharedreact/utils/LanguageSupport';

import FontsTable from 'workspaces/launcher/components/admin/FontsTable';
import FontFile from 'workspaces/launcher/data/FontFile';

type DropTargetComponentProps = {
  dropItemType: Identifier | null,
  isOver: boolean,
  
  connectDropTarget: ConnectDropTarget,
};

type Props = DropTargetComponentProps & {
  index: number,
  activeTab: number,
  language: string,
  fonts: Immutable.List<FontFile | null>,
  selection: Immutable.Set<string>,
  sortOrder: SortOrder,
  filter: string,
  filterKeywords: string[],
  allowMultiSelect: boolean,
  fontExtensionsString: string,
  needsConfirmOverwrite: boolean,
  uploadedFiles: Immutable.List<UploadedFile>,
  readOnly?: boolean,

  onDropFiles: (files: (FileList | File[])) => void,
  setFilter: (searchString: string) => void,
  setVisibleRange: (visibleRange: RangeType) => void,
  onSortOrderChange: (sortOrder: SortOrder) => void,
  onRefresh: () => void,
  onSelectionChanged: (selection: Immutable.Set<string>) => void,
  onAllowMultipleSelections: (singleSelect: boolean) => void,
  onItemSelect: (id: string) => void,
  onRemove: (id: string) => void,
  onRemoveMulti: (selection: Immutable.Set<string>) => void,
  onConfirmOverwrite: (files: (Immutable.List<UploadedFile>)) => void,
  onCancelOverwrite: (files: (Immutable.List<UploadedFile>)) => void
};

class FontsTab extends React.Component<Props> {
  constructor (props: Props) {
    super(props);

    this.handleAllowMultiSelect = this.handleAllowMultiSelect.bind(this);
  }

  override componentDidMount (): void {
    if (this.props.needsConfirmOverwrite) {
      this.askForConfirmation();
    }
  }

  override componentDidUpdate (prevProps: Readonly<Props>): void {
    if (this.props.needsConfirmOverwrite && !prevProps.needsConfirmOverwrite) {
      this.askForConfirmation();
    }
  }

  askForConfirmation (): void {
    const { uploadedFiles, onConfirmOverwrite, onCancelOverwrite } = this.props;

    askConfirmation(
      'ae_confirm_uploadfont_overwrite',
      translate('launcher.admin.fonts.confirmupload.overwrite.title'),
      translate('launcher.admin.fonts.confirmupload.overwrite.message')
    ).then(
      () => onConfirmOverwrite(uploadedFiles),
      () => onCancelOverwrite(uploadedFiles)
    );
  }

  handleAllowMultiSelect () {
    const {
      allowMultiSelect,
      onSelectionChanged,
      onAllowMultipleSelections
    } = this.props;

    if (allowMultiSelect) {
      onSelectionChanged(Immutable.Set());
    }

    onAllowMultipleSelections(!allowMultiSelect);
  }

  renderFontsTable () {
    const {
      language, fonts, selection, sortOrder,
      allowMultiSelect, onItemSelect, setVisibleRange,
      onSortOrderChange, onRemove, connectDropTarget } = this.props;

    return connectDropTarget(
      <div className='webappslauncher-fonts-table-parent'>
        <FontsTable
          language={language}
          fonts={fonts}
          selection={selection}
          allowMultiSelect={allowMultiSelect}
          sortOrder={sortOrder}
          setVisibleRange={setVisibleRange}
          onSortOrderChange={onSortOrderChange}
          onItemSelect={onItemSelect}
          onRemove={onRemove}
        />
      </div>
    );
  }

  override render (): React.ReactNode {
    const {
      language, index, activeTab,
      selection, filter, filterKeywords,
      fontExtensionsString, allowMultiSelect,
      setFilter, onRemoveMulti,
      onDropFiles, onRefresh } = this.props;

    return (
      <Tab index={index} activeIndex={activeTab}>
        <Toolbar>
          <ToolbarGroup>
            <ToolbarTitle title={translate('launcher.admin.fonts.title')} />
          </ToolbarGroup>
          <ToolbarGroup align='right'>
            {allowMultiSelect
             ? <Button id='ae_button_remove_from_folder'
               className='bi bi-trash3-fill'
               onClick={() => onRemoveMulti(selection)}
               disabled={selection.size < 1}
               title={translate('launcher.admin.fonts.tableaction.tooltip.removemulti')}
             />
             : null}
            <Button
              id='ae_button_files_multiselect'
              icon='esko_icon esko_icon-multiselect'
              onClick={this.handleAllowMultiSelect}
              active={allowMultiSelect}
              collapsable
              title={translate('common.user.action.multiselect.tooltip')}>
              {translate('common.user.action.multiselect')}
            </Button>
            <Button id='ae_button_about_refesh'
              icon='bi bi-arrow-repeat'
              onClick={onRefresh}
            />
          </ToolbarGroup>
          <ToolbarGroup align='right'>
            <SearchField id='ae_search_files'
              alignRight
              onSearch={setFilter}
              value={filter}
              keywords={filterKeywords}
              highlight={filter !== ''}
            />
          </ToolbarGroup>
        </Toolbar>
        <div className='webappslauncher-scroll-container'>
          <div className='webappslauncher-stretch-container'>
            {this.renderFontsTable()}
            <div className='webappslauncher-fonts-dropzone-parent'>
              <DropZone isLoading={false}
                instruction={translate('launcher.admin.fonts.dropzoneinstruction')}
                onDropFiles={onDropFiles}
                fileTypes={fontExtensionsString}
                acceptMultipleFiles
                readOnly={false}
                language={language}
              />
            </div>
          </div>
        </div>
      </Tab>
    );
  }
}

const target: DropTargetSpec<Props> = {
  drop (props: Props, monitor: DropTargetMonitor) {
    const files = monitor.getItem().files;
    if (files != null) {
      props.onDropFiles(files);
    }
  },
  canDrop (props: Props, monitor: DropTargetMonitor): boolean {
    return true;
  }
};

function collect (connect: DropTargetConnector, monitor: DropTargetMonitor): DropTargetComponentProps {
  return {
    connectDropTarget: connect.dropTarget(),
    dropItemType: monitor.getItemType(),
    isOver: monitor.isOver()
  };
}

export default DropTarget(
  (props: Props) => props.readOnly ? [] : [ NativeTypes.FILE ],
  target,
  collect
)(FontsTab);
